import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button";
import TextInput from "@shared/v2/TextInput";
import FieldLabel from "@shared/v2/FieldLabel";
import FieldError from "@shared/v2/FieldError";
import AiAssistant from "@shared/AiAssistant";
import SmsComposerInput from "@shared/SmsComposerInput";
import { useMessagingServiceUrl } from "../../reducers/layoutReducer/selectors";
import {
  MAX_NAME_LENGTH,
  MAX_TEMPLATE_LENGTH,
  errorMessages,
  getTextPlaceholders,
  saveTextTemplate,
  type TextTemplateDraft,
} from "./service";

export const MAX_BODY_LENGTH = MAX_TEMPLATE_LENGTH;
const TEXTAREA_ID = "text-template-body";
const NO_MESSAGES: never[] = [];

interface PlaceholderOption {
  label: string;
  value: string;
}

interface ValidationErrors {
  name?: string;
  body?: string;
}

interface TextTemplateModalProps {
  template?: TextTemplateDraft | null;
  onClose: () => void;
  onSaved: () => void;
}

const validate = ({ name, body }: { name: string; body: string }): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!name.trim()) errors.name = "is required";
  if (name.trim().length > MAX_NAME_LENGTH) errors.name = `must be ${MAX_NAME_LENGTH} characters or less`;
  if (!body.trim()) errors.body = "is required";
  if (body.length > MAX_BODY_LENGTH) errors.body = `must be ${MAX_BODY_LENGTH} characters or less`;
  return errors;
};

const TextTemplateModal = ({ template = null, onClose, onSaved }: TextTemplateModalProps) => {
  const messagingServiceUrl = useMessagingServiceUrl() as string;
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [placeholders, setPlaceholders] = useState<PlaceholderOption[]>([]);

  useEffect(() => {
    getTextPlaceholders()
      .then((data) => setPlaceholders(data.map(({ text: label, value }) => ({ label, value }))))
      .catch(() => setPlaceholders([]));
  }, []);

  useEffect(() => {
    setName(template?.templateName || "");
    setBody(template?.template || "");
    setErrors({});
    setApiErrors([]);
  }, [template]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs = validate({ name, body });
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setApiErrors([]);
    saveTextTemplate(messagingServiceUrl, { uuid: template?.uuid, templateName: name.trim(), template: body })
      .then(() => onSaved())
      .catch((err) => setApiErrors(errorMessages(err)))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[720px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col"
      show={Boolean(template)}
      onHide={onClose}
      closeOnEscape={false}
    >
      <form className="tw-flex tw-flex-col tw-gap-[32px] tw-h-full tw-overflow-hidden" onSubmit={onSubmit}>
        {/* @ts-expect-error props */}
        <Modal.Header title={`${template?.uuid ? "Edit" : "New"} Text Template`} closeButton />
        <Modal.Body className="tw-flex tw-flex-col tw-gap-[16px] tw-overflow-auto">
          {apiErrors.length > 0 && <FieldError error={apiErrors.join(", ")} />}
          <TextInput
            // @ts-expect-error props
            isRequired
            name="name"
            label="Template Name"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            error={errors.name}
          />
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-pr-[1px]">
              {/* @ts-expect-error props */}
              <FieldLabel label="Message" isRequired htmlFor={TEXTAREA_ID} />
              {/* @ts-expect-error props */}
              <AiAssistant
                align="end"
                context={{}}
                isTinyMce={false}
                side="top"
                textareaId={TEXTAREA_ID}
                messageType="SMS"
                onInsertClick={(text?: string | null) => setBody((text || "").slice(0, MAX_BODY_LENGTH))}
              />
            </div>
            <SmsComposerInput
              id={TEXTAREA_ID}
              messages={NO_MESSAGES}
              value={body}
              maxLength={MAX_BODY_LENGTH}
              maxHeight={Infinity}
              placeholderOptions={placeholders}
              showImageUpload={false}
              onChange={setBody}
              error={errors.body}
            />
            <div className="tw-flex tw-justify-end tw-items-center">
              <span className="tw-text-12d tw-text-gray-50">
                {body.length}/{MAX_BODY_LENGTH}
              </span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="tw-flex tw-flex-wrap-reverse tw-gap-[8px] tw-justify-between">
            {/* @ts-expect-error props */}
            <Button schema="secondary" size="medium" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            {/* @ts-expect-error props */}
            <Button size="medium" type="submit" isLoading={saving}>
              Save & Close
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default TextTemplateModal;
